import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Table, Dimmer, Loader, Modal, Icon } from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../../components/elements/Button';
import UploaderModal from '../../components/controls/UploaderModal';
import UploadProgress, { FileWithID } from '../../components/controls/UploadProgress';
import dayjs, { Dayjs } from 'dayjs';
import API from '../../utils/network';
import RawAPI, { AxiosRequestConfig } from 'axios';
import { DoclibFileRecord } from '../../models/GeneralModels';
import { downloadFile, formatBytes } from '../../utils/siteFunctions';
import mime from 'mime/lite';

const AccountUploadViewDocumentsPage = ({ acctNumber }: AccountUploadViewDocumentsPageProps) => {
	/// Global State
	const secondaryColor = useSelector(state => state.ui.customer.secondaryColor);

	/// Local State
	const [ docLibFiles, setDocLibFiles ] = useState<DoclibFileRecord[]>([]);
	const [ loadingFiles, setLoadingFiles ] = useState(true);
	const [ uploadModalVisible, setUploadModalVisible ] = useState(false);
	const [ uploadingFiles, setUploadingFiles ] = useState<FileWithID[]>([]);
	const [ numFilesUploaded, setNumFilesUploaded ] = useState(0);

	/// Effects
	useEffect(() => {
		API.get(`/client-portal/account/account-files/${encodeURIComponent(acctNumber)}`)
			.then(data => {
				setDocLibFiles(data.data.map(x => ({
					id: x.id,
					fileName: x.fileName,
					comments: x.comment,
					modifiedDate: dayjs(x.modifiedDate),
					fileSize: x.fileSize
				})));

				setLoadingFiles(false);
			})
			.catch(error => console.error('Error', error));
	}, [acctNumber, numFilesUploaded]);

	/// Events
	const handleUploadModalOpen = e => {
		setUploadModalVisible(true);
	};

	const handleUploadModalClose = e => {
		setUploadModalVisible(false);
	};

	const handleTriggerUpload = files => {
		const preMap = files.map((x, index) => ({
			id: uuidv4(),
			file: x
		}));

		setUploadingFiles(prevState => [ ...prevState, ...preMap.map(x => ({ id: x.id, file: x.file, progress: 0, completed: false})) ]);

		preMap.forEach((f, i) => {
			const fileExtension = f.file.name.includes('.') ? f.file.name.slice(f.file.name.lastIndexOf('.') + 1) : '';
			const mimeType = mime.getType(fileExtension);

			API.post('/account/account-file/add', {
				AccountNumber: acctNumber,
				FileLocation: 'S3',
				FileSize: f.file.size,
				FileName: f.file.name,
				FileExtension: `.${fileExtension}`,
				ModifiedDate: new Date(f.file.lastModified),
				ShowInClientPortal: true,
				FileType: mimeType
				// Comment: null,
				// EnteredById: null,
				// DoclibFolderId: null
			})
				.then(data => {
					const newRecordId: number | null = data.data.fileId;
					setNumFilesUploaded(prevState => prevState + 1);

					API.post('/account/account-file/s3-url', {
						FileId: newRecordId,
						FileExtension: fileExtension,
						ContentType: mimeType
					})
						.then(data => {
							const config: AxiosRequestConfig = {
								headers: { 'content-type': mimeType },
								onUploadProgress: e => setUploadingFiles(prevState => prevState.map(x => x.id === f.id ? { ...x, progress: e.loaded, completed: e.loaded === x.file.size } : x))
							};

							RawAPI.put(data.data.signedUrl, f.file, config)
								.then(data => { /* Nothing to do here. The progress callback in the config takes it from here. */ })
								.catch(error => {
									console.error('Error', error);
									setUploadingFiles(prevState => prevState.map(x => x.id === f.id ? { ...x, error: error } : x));
								});
						})
						.catch(error => console.error('Error', error));
				})
				.catch(error => console.error('Error', error));
		});
	};

	const handleDownloadButtonClick = (doclibFileRecord: DoclibFileRecord) => {
		const extension = doclibFileRecord.fileName.slice(doclibFileRecord.fileName.lastIndexOf('.') + 1);
		API.post('/account/account-file/s3-download-url', {
			FileId: doclibFileRecord.id,
			FileExtension: extension
		})
			.then(data => downloadFile(data.data.signedUrl, doclibFileRecord.fileName))
			.catch(error => console.error(error));
	};

	/// Render
	return (
		<>
			<h1
				style={{ display: 'inline-block' }}
			>
				View Uploaded Documents
			</h1>
			<UploaderModal
				onOpen={handleUploadModalOpen}
				onClose={handleUploadModalClose}
				open={uploadModalVisible}
				onTriggerUpload={handleTriggerUpload}
				trigger={(
					<Button lookLikeAnchor>Add New</Button>
				)}
			/>
			{ loadingFiles ? (
				<div
					style={{
						borderTop: `2px solid ${secondaryColor}`,
						position: 'relative',
						marginTop: '1em',
						height: '10em'
					}}
				>
					<Dimmer active inverted style={{ zIndex: 'unset' }}>
						<Loader size='massive'>Loading</Loader>
					</Dimmer>
				</div>
			) : (
				<>
					<div
						style={{
							borderTop: `2px solid ${secondaryColor}`,
							marginTop: '1em'
						}}
					>
						<Table celled striped>
							<Table.Header>
								<Table.Row>
									<Table.HeaderCell collapsing></Table.HeaderCell>
									<Table.HeaderCell>File Name</Table.HeaderCell>
									<Table.HeaderCell>Comments</Table.HeaderCell>
									<Table.HeaderCell collapsing>Modified</Table.HeaderCell>
									<Table.HeaderCell collapsing>File Size</Table.HeaderCell>
									<Table.HeaderCell collapsing />
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{
									docLibFiles.map(x => (
										<Table.Row key={x.id}>
											<Table.Cell collapsing><Icon className={iconMapping[x.fileName.slice(x.fileName.lastIndexOf('.') + 1)] || 'file'} size='large' /></Table.Cell>
											<Table.Cell>{x.fileName}</Table.Cell>
											<Table.Cell>{x.comments}</Table.Cell>
											<Table.Cell collapsing>{x.modifiedDate.format('M/D/YYYY')}</Table.Cell>
											<Table.Cell collapsing>{formatBytes(x.fileSize)}</Table.Cell>
											<Table.Cell collapsing>
												<Button
													basic
													compact
													icon
													borderless
													onClick={() => handleDownloadButtonClick(x)}
												>
													<Icon
														name='download'
														fitted
														size="large"
													/>
												</Button>
											</Table.Cell>
										</Table.Row>
									))
								}
							</Table.Body>
						</Table>
					</div>
					{uploadingFiles.length > 0 && (
						<>
							<h2>Uploading Files</h2>
							<UploadProgress uploadingFiles={uploadingFiles} />
						</>
					)}
				</>
			) }
		</>
	);
};

const iconMapping = {
	'pdf': 'file pdf',
	'zip': 'file archive',
	'doc': 'file word',
	'docx': 'file word',
	'png': 'file image',
	'jpg': 'file image',
	'jpeg': 'file image',
	'csv': 'file excel',
	'xls': 'file excel',
	'xlsx': 'file excel',
	'txt': 'file text',
	'ppt': 'file powerpoint',
	'pptx': 'file powerpoint',
	'htm': 'file code',
	'html': 'file code'
};

interface AccountUploadViewDocumentsPageProps {
	acctNumber: string
}

export default AccountUploadViewDocumentsPage;